import _get from 'lodash/get';
import { call, put } from 'redux-saga/effects';
import { get } from 'ravenjs/utils/lodash';

import { decodeTokenAPI, fetchTokenAPI, refreshTokenAPI } from 'modules/apis';
import { getExpiresInTimestamp, getTokenUserId } from 'utils/auth';
import { isMFAEnabled, isMFARequiredResponse } from 'utils/mfa';
import * as authActions from '../actions';

/**
 * Extract the token from an authenticate response, along with whether the API
 * requires the user to complete an MFA flow instead of signing them in.
 * A short lived `mfa_token` is owned by the `mfa` module, so it must never be
 * stored in the (persisted) `auth` state.
 *
 * @method getToken
 * @param  {Object}  resp The authenticate response
 * @return {Object}       The token and its `isMFARequired` flag
 */
function getToken(resp) {
    const token = _get(resp, 'data', {});

    return {
        isMFARequired: isMFAEnabled() && isMFARequiredResponse(token),
        token,
    };
}

/**
 * Fetch the oAuth token for a given set of user credentials.
 *
 * @method fetchTokenSaga
 * @type   {Generator}
 * @param  {Object}        action The redux action
 * @return {Object|Error}
 */
export function* fetchTokenSaga(action) {
    try {
        // Extract the userCreds from the action.
        const { payload: userCreds } = action;
        // Make the call to fetch the `oAuthToken`.
        const resp = yield call(fetchTokenAPI, userCreds);
        // Extract the token from the valid response.
        const { isMFARequired, token } = getToken(resp);
        // Let the caller start the MFA flow, the sign in is not complete yet.
        if (isMFARequired) {
            return token;
        }
        // Dispatch the `fetchToken` SUCCESS action.
        yield put(authActions.fetchToken.success(token));
        // Return the token.
        return token;
    } catch (error) {
        // Otherwise catch the error and dispatch the `fetchToken` ERROR action.
        yield put(authActions.fetchToken.error(error));
        // Return the error
        return error;
    }
}

/**
 * Decode the given oAuth token.
 *
 * @method decodeTokenSaga
 * @type   {Generator}
 * @return {Object|Error}
 */
export function* decodeTokenSaga() {
    try {
        const resp = yield call(decodeTokenAPI);
        const decodedToken = _get(resp, 'data', {});
        // Extract the expires_in UNIX timestamp (in sec.) for the token,
        // convert it into milliseconds for JavaScript purposes.
        const expiresInTimestamp = getExpiresInTimestamp(decodedToken);
        const userId = getTokenUserId(decodedToken);
        yield put(
            authActions.decodeToken.success({ expires_in_timestamp: expiresInTimestamp, userId })
        );
        return decodedToken;
    } catch (error) {
        yield put(authActions.decodeToken.error(error));
        return error;
    }
}

/**
 * Refresh the oAuth token
 *
 * @method refreshTokenSaga
 * @type   {Generator}
 */
export function* refreshTokenSaga() {
    try {
        const response = yield call(refreshTokenAPI);
        const authToken = get(response, 'data', {});
        yield put(authActions.fetchToken.success(authToken));
        const accessToken = authToken.access_token;
        yield call(decodeTokenSaga, { payload: accessToken });
        yield put(authActions.refreshToken.success(accessToken));
    } catch (error) {
        yield put(authActions.refreshToken.error(error));
    }
}
